import { Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import type { RowSelectionState } from '@tanstack/react-table';
import { useState } from 'react';
import { useComplianceAlerts } from '@/hooks/queries/compliance/use-compliance-alerts';
import { getErrorMessage } from '@/lib/api/error';
import type { AlertType, ComplianceAlert } from '@/services/api/compliance-alerts';
import { ALERT_TYPE_TITLES, BULK_DISMISS_MAX_ALERTS } from '@/services/api/compliance-alerts';
import { AlertDetailDialog } from './alert-detail-dialog';
import { AlertsTable } from './alerts-table';
import { BulkDismissBar } from './bulk-dismiss-bar';

interface AlertsCardProps {
  alertType?: AlertType;
  /** Scope the list to one user (used on the user console) */
  accountHolderId?: string;
}

export const AlertsCard = ({ alertType = 'alert', accountHolderId }: AlertsCardProps) => {
  const { data: alerts, isPending, isError, error, refetch } = useComplianceAlerts(alertType, accountHolderId);

  const [openAlert, setOpenAlert] = useState<ComplianceAlert | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [visibleSelectedAlerts, setVisibleSelectedAlerts] = useState<ComplianceAlert[]>([]);

  const isBulkDismissAvailable = alertType === 'alert';
  const visibleSelectedIds = new Set(visibleSelectedAlerts.map((alert) => alert.alert_id));
  const hiddenSelectedCount = (alerts ?? []).filter((alert) => rowSelection[alert.alert_id] && !visibleSelectedIds.has(alert.alert_id)).length;
  const hasTickedAlerts = visibleSelectedAlerts.length + hiddenSelectedCount > 0;

  const deselectAlerts = (dismissedAlerts: ComplianceAlert[]) => {
    const dismissedIds = new Set(dismissedAlerts.map((alert) => alert.alert_id));

    setRowSelection((current) => Object.fromEntries(Object.entries(current).filter(([alertId]) => !dismissedIds.has(alertId))));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{ALERT_TYPE_TITLES[alertType]}s</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-52 flex-col gap-3">
        {isError ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-red-500">Failed to load alerts: {getErrorMessage(error)}</p>
            <Button onClick={() => refetch()} size="sm" type="button" variant="outline">
              Retry
            </Button>
          </div>
        ) : (
          <>
            <AlertsTable
              alerts={alerts ?? []}
              alertType={alertType}
              enableSelectAll={false}
              isLoading={isPending}
              isRowSelectable={() => visibleSelectedAlerts.length < BULK_DISMISS_MAX_ALERTS}
              onRowClick={setOpenAlert}
              onRowSelectionChange={setRowSelection}
              onSelectionChange={setVisibleSelectedAlerts}
              rowSelection={rowSelection}
              selectable={isBulkDismissAvailable}
            />
            {isBulkDismissAvailable && hasTickedAlerts ? (
              <BulkDismissBar
                alertType={alertType}
                hiddenSelectedCount={hiddenSelectedCount}
                onClearSelection={() => setRowSelection({})}
                onDismissed={deselectAlerts}
                selectedAlerts={visibleSelectedAlerts}
              />
            ) : null}
          </>
        )}
      </CardContent>
      {openAlert ? <AlertDetailDialog alert={openAlert} alertType={alertType} onClose={() => setOpenAlert(null)} onDismissed={() => setOpenAlert(null)} /> : null}
    </Card>
  );
};
AlertsCard.displayName = 'AlertsCard';
